import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ---------- Accounts (план счетов) ----------
export interface Account {
  id: string;
  code: string;
  name: string;
  description: string | null;
  account_type: 'A' | 'P' | 'AP';
  is_group_only: boolean;
  is_off_balance: boolean;
  use_currency: boolean;
  use_quantity: boolean;
  do_not_revaluate: boolean;
}

export interface AccountUpdate {
  name?: string;
  description?: string | null;
  do_not_revaluate?: boolean;
}

export const accounts = {
  list: (params?: { q?: string; chart_code?: string; only_postable?: boolean; off_balance?: boolean; limit?: number }) =>
    api.get<Account[]>('/accounts', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Account>(`/accounts/${id}`).then((r) => r.data),
  update: (id: string, payload: AccountUpdate) =>
    api.patch<Account>(`/accounts/${id}`, payload).then((r) => r.data),
};

// ---------- Manual operations (бухгалтерская справка) ----------
export interface ManualOperationEntry {
  id?: string;
  line_number: number;
  dt_account_id: string;
  dt_subconto_1_id?: string | null;
  dt_subconto_1_type?: string | null;
  cr_account_id: string;
  cr_subconto_1_id?: string | null;
  cr_subconto_1_type?: string | null;
  amount: string; // numeric — передаём строкой, чтобы не терять точность
  content?: string | null;
  project_id?: string | null;
}

export interface ManualOperationListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3f:
  state?: CancellableState;
}

export interface ManualOperation extends ManualOperationListItem {
  posted_at: string | null;
  comment: string | null;
  entries: ManualOperationEntry[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ManualOperationCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content?: string | null;
  comment?: string | null;
  entries: ManualOperationEntry[];
  project_id?: string | null;
  // Фаза 2.1 #3: subtype для специфічних flows (batch_correction).
  operation_subtype?: string | null;
  related_batch_key_id?: string | null;
  new_cost_price?: string | null;
}

export interface ManualOperationUpdate {
  document_number?: string;
  document_date?: string;
  operation_content?: string | null;
  comment?: string | null;
  entries?: ManualOperationEntry[];
  project_id?: string | null;
  operation_subtype?: string | null;
  related_batch_key_id?: string | null;
  new_cost_price?: string | null;
}

export const manualOps = {
  list: (params?: { organization_id?: string; posted?: boolean; limit?: number; offset?: number }) =>
    api.get<Page<ManualOperationListItem>>('/manual-operations', { params }).then((r) => r.data),
  get: (id: string) => api.get<ManualOperation>(`/manual-operations/${id}`).then((r) => r.data),
  create: (payload: ManualOperationCreate) =>
    api.post<ManualOperation>('/manual-operations', payload).then((r) => r.data),
  update: (id: string, payload: ManualOperationUpdate) =>
    api.patch<ManualOperation>(`/manual-operations/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/manual-operations/${id}`).then(() => undefined),
  post: (id: string) => api.post<ManualOperation>(`/manual-operations/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<ManualOperation>(`/manual-operations/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/manual-operations/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ---------- Trial balance (ОСВ) ----------
export interface TrialBalanceRow {
  account_code: string;
  account_name: string;
  account_type: string;
  parent_code: string | null;
  is_group: boolean;
  opening_balance_dt: string;
  opening_balance_cr: string;
  turnover_dt: string;
  turnover_cr: string;
  closing_balance_dt: string;
  closing_balance_cr: string;
}

export interface TrialBalance {
  organization_id: string;
  date_from: string;
  date_to: string;
  rows: TrialBalanceRow[];
  totals: { turnover_dt: string; turnover_cr: string };
}

export interface GoodsBalanceRow {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_article: string | null;
  nomenclature_name: string;
  quantity: string;
  cost: string | null;
}

export interface GoodsBalance {
  organization_id: string;
  on_date: string;
  warehouse_id: string | null;
  rows: GoodsBalanceRow[];
  totals: { quantity: string; cost: string };
}

export interface GoodsBatchRow {
  warehouse_id: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_name: string;
  batch_key_id: string;
  receipt_document_type: string;
  receipt_document_id: string;
  receipt_date: string;
  cost_price: string;
  quantity: string;
  cost: string;
}

export interface GoodsBatches {
  organization_id: string;
  on_date: string;
  rows: GoodsBatchRow[];
}

export interface AccountCardRow {
  entry_date: string;
  document_type: string;
  document_number: string | null;
  document_date: string | null;
  side: 'DT' | 'CR' | string;
  corr_account_code: string;
  content: string | null;
  amount_dt: string;
  amount_cr: string;
  running_balance: string;
}

export interface AccountCard {
  organization_id: string;
  account_code: string;
  date_from: string;
  date_to: string;
  rows: AccountCardRow[];
  totals: { turnover_dt: string; turnover_cr: string };
}

export interface SubcontoAnalysisRow {
  subconto_type: string;
  subconto_id: string | null;
  subconto_name: string;
  opening_balance_dt: string;
  opening_balance_cr: string;
  turnover_dt: string;
  turnover_cr: string;
  closing_balance_dt: string;
  closing_balance_cr: string;
}

export interface SubcontoAnalysis {
  organization_id: string;
  account_code: string;
  account_type: string;
  date_from: string;
  date_to: string;
  rows: SubcontoAnalysisRow[];
  totals: {
    turnover_dt: string;
    turnover_cr: string;
    closing_balance_dt: string;
    closing_balance_cr: string;
  };
}

export interface ReconciliationRow {
  movement_date: string;
  document_type: string;
  document_id: string;
  document_number: string | null;
  direction: string;
  debit: string;
  credit: string;
  balance: string;
}

export interface Reconciliation {
  organization_id: string;
  contractor_id: string;
  contract_id: string | null;
  date_from: string;
  date_to: string;
  opening_balance: string;
  rows: ReconciliationRow[];
  closing_balance: string;
  turnover_debit: string;
  turnover_credit: string;
}

export interface ProductionWipRow {
  department_id: string | null;
  department_name: string | null;
  nomenclature_group_id: string | null;
  nomenclature_group_name: string | null;
  balance: string;
}
export interface ProductionWipReport {
  as_of: string;
  rows: ProductionWipRow[];
  total: string;
}

export const reports = {
  trialBalance: (params: {
    organization_id: string;
    date_from: string;
    date_to: string;
    chart_code?: string;
  }) => api.get<TrialBalance>('/reports/trial-balance', { params }).then((r) => r.data),
  productionWip: (params: {
    organization_id: string;
    as_of?: string;
  }) => api.get<ProductionWipReport>('/reports/production-wip', { params }).then((r) => r.data),
  goodsBalance: (params: {
    organization_id: string;
    on_date: string;
    warehouse_id?: string;
  }) => api.get<GoodsBalance>('/reports/goods-balance', { params }).then((r) => r.data),
  goodsBatches: (params: {
    organization_id: string;
    on_date: string;
  }) => api.get<GoodsBatches>('/reports/goods-batches-balance', { params }).then((r) => r.data),
  accountCard: (params: {
    organization_id: string;
    account_code: string;
    date_from: string;
    date_to: string;
    chart_code?: string;
  }) => api.get<AccountCard>('/reports/account-card', { params }).then((r) => r.data),
  subcontoAnalysis: (params: {
    organization_id: string;
    account_code: string;
    date_from: string;
    date_to: string;
    chart_code?: string;
  }) => api.get<SubcontoAnalysis>('/reports/subconto-analysis', { params }).then((r) => r.data),
  vatSummary: (params: {
    organization_id: string;
    period_year: number;
    period_month: number;
  }) => api.get<VatSummary>('/reports/vat-summary', { params }).then((r) => r.data),
  goodsReserved: (params: {
    organization_id: string;
    on_date: string;
  }) => api.get<GoodsReserved>('/reports/goods-reserved', { params }).then((r) => r.data),
  // ПРОДАЖ-розбивка Етап 4 (2026-06-03): агрегована аналітика 4 показника.
  inventoryAnalytics: (params: {
    organization_id: string;
    on_date: string;
    warehouse_id?: string;
    q?: string;
  }) => api.get<InventoryAnalytics>('/reports/inventory-analytics', { params }).then((r) => r.data),
  reconciliationAct: (params: {
    organization_id: string;
    contractor_id: string;
    contract_id?: string;
    date_from: string;
    date_to: string;
  }) => api.get<Reconciliation>('/reports/reconciliation-act', { params }).then((r) => r.data),
  contractorBalance: (params: {
    organization_id: string;
    contractor_id: string;
    contract_id: string;
    account_code: string;
    on_date?: string;
  }) => api.get<{ debit: string; credit: string; balance_signed: string }>(
    '/reports/contractor-balance', { params }
  ).then((r) => r.data),
  vatDeclarationXml: async (params: {
    organization_id: string;
    period_year: number;
    period_month: number;
  }): Promise<Blob> => {
    const r = await api.get('/reports/vat-declaration.xml', {
      params,
      responseType: 'blob',
    });
    return r.data as Blob;
  },
};

export interface VatSummaryRateRow {
  rate_code: string;
  rate_label: string;
  base: string;
  vat: string;
}

export interface VatSummary {
  organization_id: string;
  period_year: number;
  period_month: number;
  obligations: VatSummaryRateRow[];
  credit: VatSummaryRateRow[];
  total_obligations: string;
  total_credit: string;
  to_pay: string;
  to_refund: string;
}

export interface GoodsReservedRow {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_name: string;
  customer_order_id: string;
  customer_order_number: string;
  customer_order_date: string;
  contractor_id: string;
  contractor_name: string;
  quantity: string;
}

export interface GoodsReserved {
  organization_id: string;
  on_date: string;
  rows: GoodsReservedRow[];
}

// ПРОДАЖ-розбивка Етап 4 (2026-06-03): агрегована аналітика наявності.
export interface InventoryAnalyticsRow {
  warehouse_id: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  nomenclature_id: string;
  nomenclature_code: string;
  nomenclature_name: string;
  uom_short: string | null;
  stock: string;       // Залишок
  reserved: string;    // Резерв
  in_transit: string;  // В дорозі
  demanded: string;    // Попит
  available: string;   // Залишок - Резерв
}

export interface InventoryAnalytics {
  organization_id: string;
  on_date: string;
  rows: InventoryAnalyticsRow[];
}
